import { useEffect, useRef } from 'react';

/**
 * Keyboard shortcuts, Gmail-style.
 *
 * The keymap is data, not code: a `ShortcutMap` is an object of
 * `binding -> handler`, so adding a shortcut (or letting a plugin contribute
 * one, see src/plugins/types.ts) is a one-line change with no new listener.
 *
 * Binding syntax: `"j"`, `"shift+/"`, `"mod+Enter"` (mod = Cmd on macOS, Ctrl
 * elsewhere), or a sequence like `"g i"` (press g, then i — Gmail's "go to
 * inbox"). Bindings are matched case-insensitively against `event.key`.
 */

export type ShortcutHandler = (event: KeyboardEvent) => void;
export type ShortcutMap = Record<string, ShortcutHandler | undefined>;

export interface ShortcutOptions {
  /** Turn the whole map off (e.g. while a modal owns the keyboard). */
  enabled?: boolean;
  /**
   * Bindings that must still fire while a text field has focus — `/` does NOT,
   * but Escape and mod+Enter (send) do.
   */
  allowInInputs?: string[];
  /** ms allowed between the two keys of a sequence binding. */
  sequenceTimeout?: number;
  /**
   * A PAGE keymap (Mail's, Calendar's): stand aside for any key pressed
   * inside a modal dialog or a menu.
   *
   * `enabled` alone cannot do this for the key that closes the modal: the
   * dialog's own Escape handler runs first and sets state, but this listener
   * is on `window` and sees the same event before React re-renders with
   * `enabled: false`. So Escape closed the label picker AND closed the open
   * message behind it; the reading pane's toolbar unmounted and focus fell to
   * <body> instead of returning to the button that opened the picker (#205).
   */
  ignoreInsideModals?: boolean;
}

const MODAL_OR_MENU = '[aria-modal="true"], [role="alertdialog"], dialog[open], [role="menu"]';

const SEQUENCE_TIMEOUT_MS = 900;

/** Human-readable descriptions, rendered by the shortcut help sheet (?). */
export interface ShortcutDoc {
  keys: string;
  label: string;
  group: string;
}

/**
 * The application keymap, in one place.
 *
 * Keep this list and the handler wiring in sync; `ShortcutDoc[]` is what the
 * "Keyboard shortcuts" help sheet renders, so a documented shortcut that is
 * not wired up is a visible bug.
 */
export const GLOBAL_SHORTCUT_DOCS: ShortcutDoc[] = [
  { keys: 'j', label: 'Next message', group: 'Navigation' },
  { keys: 'k', label: 'Previous message', group: 'Navigation' },
  { keys: 'Enter', label: 'Open message', group: 'Navigation' },
  { keys: 'shift+Enter', label: 'Open message in a new window', group: 'Navigation' },
  { keys: 'u', label: 'Back to list', group: 'Navigation' },
  // Pane cycling (#205). F6 first: on macOS browsers ⌘⌥←/→ is the tab
  // switcher and may never reach the page.
  { keys: 'F6', label: 'Next pane (folders, list, message)', group: 'Navigation' },
  { keys: 'shift+F6', label: 'Previous pane', group: 'Navigation' },
  { keys: 'Ctrl/⌘+alt+→', label: 'Next pane (alternative)', group: 'Navigation' },
  { keys: 'Ctrl/⌘+alt+←', label: 'Previous pane (alternative)', group: 'Navigation' },
  { keys: 'g then i', label: 'Go to Inbox', group: 'Navigation' },
  { keys: 'g then s', label: 'Go to Starred', group: 'Navigation' },
  { keys: 'g then c', label: 'Go to Calendar', group: 'Navigation' },
  // One binding per mailbox that exists, in the switcher's order (MailView).
  { keys: 'Ctrl/⌘+1…9', label: 'Switch to mailbox 1 to 9', group: 'Navigation' },
  { keys: 'e', label: 'Archive', group: 'Actions' },
  { keys: 'Delete', label: 'Delete', group: 'Actions' },
  { keys: '!', label: 'Report spam / Not spam', group: 'Actions' },
  { keys: 'z', label: 'Undo archive, delete or move', group: 'Actions' },
  { keys: 'x', label: 'Select or deselect message', group: 'Actions' },
  { keys: 'Ctrl/⌘+a', label: 'Select all messages in the list', group: 'Actions' },
  { keys: 'r', label: 'Reply', group: 'Actions' },
  { keys: 'a', label: 'Reply all', group: 'Actions' },
  { keys: 'f', label: 'Forward', group: 'Actions' },
  { keys: 'c', label: 'Compose', group: 'Actions' },
  { keys: 's', label: 'Toggle star', group: 'Actions' },
  { keys: 'shift+u', label: 'Mark unread', group: 'Actions' },
  { keys: 'shift+i', label: 'Mark read', group: 'Actions' },
  { keys: 'v', label: 'Move to folder', group: 'Actions' },
  { keys: 'l', label: 'Label as', group: 'Actions' },
  { keys: 'shift+t', label: 'Add to Tasks', group: 'Actions' },
  { keys: 'b', label: 'Snooze', group: 'Actions' },
  { keys: 'shift+F10', label: 'Message menu', group: 'Actions' },
  // Bound by the reading pane only while the AI assistant is on (ADR 0074).
  { keys: 'shift+s', label: 'Summarize with AI', group: 'Actions' },
  { keys: '/', label: 'Focus search', group: 'Application' },
  { keys: '?', label: 'Keyboard shortcuts', group: 'Application' },
  { keys: 'Escape', label: 'Dismiss', group: 'Application' },
];

/** Exported so UI that *names* a shortcut spells it the way the key behaves. */
export const isMac = (): boolean =>
  typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

function normalise(event: KeyboardEvent): string {
  const parts: string[] = [];
  const mod = isMac() ? event.metaKey : event.ctrlKey;
  if (mod) parts.push('mod');
  if (event.altKey) parts.push('alt');
  /*
   * Shift is only meaningful for keys that do not already change with it: a
   * letter ("u" vs "shift+u") and a named key ("Enter" vs "shift+Enter") need
   * the modifier to be distinguishable, while "#", "?" and "/" ARE the shifted
   * character and must not grow a redundant "shift+" that no binding names.
   *
   * The test for "a letter" is case-foldability rather than a Latin range, so
   * it behaves on non-Latin layouts instead of quietly treating every such key
   * as punctuation.
   */
  const named = event.key.length > 1;
  const foldable = !named && event.key.toLowerCase() !== event.key.toUpperCase();
  if (event.shiftKey && (named || foldable)) parts.push('shift');
  parts.push(named ? event.key : event.key.toLowerCase());
  return parts.join('+');
}

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * A control that Enter or Space already means something to: a link, a button,
 * a menu item. On one of those the key is the control's, never a page
 * shortcut's. Without this, Enter on the skip link (or on any toolbar button)
 * opened the message under the list's cursor instead of doing what the
 * focused thing says (WCAG 2.1.1).
 */
const ACTIVATABLE =
  'a[href], button, summary, [role="button"], [role="link"], [role="menuitem"], [role="menuitemcheckbox"], ' +
  '[role="menuitemradio"], [role="option"], [role="tab"], [role="checkbox"], [role="radio"], [role="switch"], ' +
  '[role="treeitem"]';

function isActivatable(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(ACTIVATABLE) !== null;
}

export function useKeyboardShortcuts(map: ShortcutMap, options: ShortcutOptions = {}): void {
  // A NAMED key keeps its own spelling through `normalise` ("Enter", not
  // "enter"), so a binding has to be written the way the event reports it.
  // This default used to say 'mod+enter', which never matched anything and
  // silently disabled ⌘↵ inside every input it was meant to allow.
  const {
    enabled = true,
    allowInInputs = ['Escape', 'mod+Enter'],
    sequenceTimeout = SEQUENCE_TIMEOUT_MS,
    ignoreInsideModals = false,
  } = options;

  // Keeps handlers fresh without re-binding the listener on every render.
  const mapRef = useRef(map);
  mapRef.current = map;
  const allowRef = useRef(allowInInputs);
  allowRef.current = allowInInputs;

  useEffect(() => {
    if (!enabled) return;
    let pendingPrefix: string | null = null;
    let pendingTimer: ReturnType<typeof setTimeout> | undefined;

    const clearPrefix = () => {
      pendingPrefix = null;
      if (pendingTimer) clearTimeout(pendingTimer);
      pendingTimer = undefined;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        ignoreInsideModals &&
        event.target instanceof Element &&
        (event.target.closest(MODAL_OR_MENU) !== null ||
          // Focus fell out of an open modal onto the page (its focused
          // button unmounted): the keys still belong to the modal, never
          // to the view underneath it.
          ((event.target === document.body || event.target === document.documentElement) &&
            document.querySelector('[aria-modal="true"]') !== null))
      ) {
        clearPrefix();
        return;
      }
      const binding = normalise(event);
      const inText = isTextEntry(event.target);
      if (inText && !allowRef.current.includes(binding)) {
        clearPrefix();
        return;
      }
      if ((binding === 'Enter' || binding === ' ') && isActivatable(event.target)) {
        clearPrefix();
        return;
      }

      // Second key of a sequence ("g i").
      if (pendingPrefix) {
        const sequence = `${pendingPrefix} ${binding}`;
        clearPrefix();
        const seqHandler = mapRef.current[sequence];
        if (seqHandler) {
          event.preventDefault();
          seqHandler(event);
          return;
        }
      }

      // A key that starts a known sequence: wait for the second press.
      const startsSequence = Object.keys(mapRef.current).some((key) =>
        key.startsWith(`${binding} `),
      );
      if (startsSequence) {
        pendingPrefix = binding;
        pendingTimer = setTimeout(clearPrefix, sequenceTimeout);
        event.preventDefault();
        return;
      }

      const handler = mapRef.current[binding];
      if (handler) {
        event.preventDefault();
        handler(event);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearPrefix();
    };
  }, [enabled, sequenceTimeout, ignoreInsideModals]);
}
